/**
 * Cross-visit patient image retrieval (PRD.253 / PRD.254).
 *
 * Shared helpers that split a patient's uploaded images into the active
 * encounter ("This Visit") and prior encounters ("Past Visits"), attaching
 * visit-date context so pickers can show where a historical image came from.
 *
 * This module deliberately depends only on the data-access layer (no drawing
 * or compare imports) so both the Compare picker (compare-asset-utils) and the
 * Draw picker (DrawCharting) can reuse it without a circular dependency.
 */

import { useMemo } from 'react';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qPatientFiles,
  qEncountersByPatient,
  qVisitsByPatient,
  type PatientFileRow,
  type PatientEncounterRow,
  type PatientVisitRow,
} from '@helios/data-access';

/**
 * Map each encounter to its best available visit date: prefer the owning
 * visit's `visit_date`, then fall back to the encounter's own start/created
 * timestamp. Used to label and sort past-visit images.
 */
export function useVisitDateByEncounter(patientId: string): Map<string, string | null> {
  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncountersByPatient(patientId),
  );
  const { data: visitRows = [] } = usePowerSyncQuery<PatientVisitRow>(qVisitsByPatient(patientId));

  return useMemo(() => {
    const visitDateById = new Map<string, string | null>();
    for (const v of visitRows) visitDateById.set(v.id, v.visit_date ?? v.started_at ?? null);
    const m = new Map<string, string | null>();
    for (const e of encounterRows) {
      const fromVisit = e.visit_id ? visitDateById.get(e.visit_id) : null;
      m.set(e.id, fromVisit ?? e.started_at ?? e.created_at ?? null);
    }
    return m;
  }, [encounterRows, visitRows]);
}

export interface VisitImageGroup {
  encounterId: string;
  visitId: string | null;
  /** 'YYYY-MM-DD' or ISO timestamp — best available date for the visit. */
  visitDate: string | null;
  images: PatientFileRow[];
}

export interface UsePatientImagesByVisitResult {
  /** Every uploaded image for the patient, newest first. For hydration/resolution. */
  allImages: PatientFileRow[];
  /** id → row, so a saved base-image reference resolves regardless of visit. */
  fileById: Map<string, PatientFileRow>;
  /** Images from the active encounter ("This Visit"). Newest first. */
  currentImages: PatientFileRow[];
  /** Past-visit images grouped by originating encounter, newest visit first. */
  pastGroups: VisitImageGroup[];
}

/**
 * Aggregate a patient's uploaded images across ALL visits (PRD.254), split into
 * the active encounter and prior encounters. Only uploaded photos
 * (`kind='image'` with a URL) are returned — drawings/templates are out of
 * scope for the Draw base-image picker.
 */
export function usePatientImagesByVisit(
  patientId: string,
  currentEncounterId: string,
): UsePatientImagesByVisitResult {
  const { data: fileRows = [] } = usePowerSyncQuery<PatientFileRow>(qPatientFiles(patientId));
  const visitDateByEncounter = useVisitDateByEncounter(patientId);

  const allImages = useMemo(() => fileRows.filter((f) => f.kind === 'image' && f.url), [fileRows]);

  const fileById = useMemo(() => {
    const m = new Map<string, PatientFileRow>();
    for (const f of fileRows) m.set(f.id, f);
    return m;
  }, [fileRows]);

  const currentImages = useMemo(
    () => allImages.filter((f) => f.encounter_id === currentEncounterId),
    [allImages, currentEncounterId],
  );

  const pastGroups = useMemo<VisitImageGroup[]>(() => {
    const byEncounter = new Map<string, VisitImageGroup>();
    for (const f of allImages) {
      const enc = f.encounter_id;
      if (!enc || enc === currentEncounterId) continue;
      let group = byEncounter.get(enc);
      if (!group) {
        group = {
          encounterId: enc,
          visitId: f.visit_id,
          visitDate: visitDateByEncounter.get(enc) ?? f.created_at ?? null,
          images: [],
        };
        byEncounter.set(enc, group);
      }
      group.images.push(f);
    }
    return [...byEncounter.values()].sort((x, y) =>
      (y.visitDate ?? '').localeCompare(x.visitDate ?? ''),
    );
  }, [allImages, currentEncounterId, visitDateByEncounter]);

  return { allImages, fileById, currentImages, pastGroups };
}

/** Format a visit date (YYYY-MM-DD or ISO) for display in a picker. */
export function formatVisitDate(date: string | null): string {
  if (!date) return 'Unknown date';
  const parsed = new Date(date.length <= 10 ? `${date}T00:00:00` : date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
